import { isEmpty } from 'lodash';

import i18n from '@/i18n';
import CourseApi from '../backend/CourseApi';
import CampusesApi from '../backend/CampusesApi';
import SportsApi from '../backend/SportsApi';
import AuthenticationApi from '../backend/AuthenticationApi';
import StudentApi from '../backend/StudentApi';
import SubscriptionsApi from '../backend/SubscriptionsApi';
import ProgramApi from '../backend/ProgramApi';
import CheckApi from '../backend/CheckApi';
import axios from '../backend/axios';
import * as MutationTypes from './mutation-types';
import router from '../router';

const actions = {
  // Courses
  fetchCourses({ commit }) {
    CourseApi.list().then((response) => {
      commit(MutationTypes.SET_COURSES, response.data);
    });
  },
  fetchCampuses({ commit }) {
    CampusesApi.list().then((response) => {
      commit(MutationTypes.SET_CAMPUSES, response.data);
    });
  },
  fetchSports({ commit }) {
    SportsApi.list().then((response) => {
      commit(MutationTypes.SET_SPORTS, response.data);
    });
  },
  fetchCourse({ commit, state }) {
    commit(MutationTypes.RESET_COURSE);
    CourseApi.get(state.route.params.courseId).then((response) => {
      commit(MutationTypes.SET_COURSE, response.data);
    });
  },
  setCoursesFilter({ commit }, payload) {
    commit(MutationTypes.SET_COURSES_FILTER, payload);
  },
  resetFilters({ commit }) {
    commit(MutationTypes.SET_COURSES_FILTER, { filter: 'sport', value: [] });
    commit(MutationTypes.SET_COURSES_FILTER, { filter: 'campus', value: undefined });
    commit(MutationTypes.SET_COURSES_FILTER, { filter: 'availability', value: undefined });
  },

  // subscriptions
  setSubscriptionLoading({ commit }, value) {
    commit(MutationTypes.SET_SUBSCRIPTION_LOADING, value);
  },
  setPendingSubscription({ commit }, payload) {
    commit(MutationTypes.SET_PENDING_SUBSCRIPTION, payload);
  },
  subscriptionConflict({ commit }) {
    commit(MutationTypes.SET_ALERT, {
      show: true,
      text: i18n.t('You are already subscribed to this course'),
      color: 'error',
      timeout: 3000,
    });
  },
  subscriptionLimitReached({ commit }) {
    commit(MutationTypes.SET_ALERT, {
      show: true,
      text: i18n.t('You reached the subscription limit'),
      color: 'error',
      timeout: 3000,
    });
  },
  confirmPendingSubscription({ state, dispatch, commit }) {
    const { courseId } = state.pendingSubscription;
    CourseApi.subscribe(courseId)
      .then(() => {
        router.push(`/courses/${courseId}/confirmed`);
        commit(MutationTypes.RESET_PENDING_SUBSCRIPTION);
      })
      .catch((error) => {
        if (error.response.status === 409) {
          dispatch('subscriptionConflict');
        } else if (error.response.status === 422) {
          dispatch('subscriptionLimitReached');
        }
      }).finally(() => {
        commit(MutationTypes.SET_SUBSCRIPTION_LOADING, false);
      });
  },
  confirmSubscription({ commit, state, dispatch }) {
    const { courseId } = state.route.params;
    CourseApi.subscribe(courseId).then(() => {
      router.push(`/courses/${courseId}/confirmed`);
      commit(MutationTypes.RESET_PENDING_SUBSCRIPTION);
    }).catch((error) => {
      if (error.response.status === 401) {
        commit(MutationTypes.SET_PENDING_SUBSCRIPTION, {
          courseId,
        });
        router.push(`/sign_in?redirect=/subscribe/${courseId}`);
      } else if (error.response.status === 409) {
        dispatch('subscriptionConflict');
      } else if (error.response.status === 422) {
        dispatch('subscriptionLimitReached');
      }
    }).finally(() => {
      commit(MutationTypes.SET_SUBSCRIPTION_LOADING, false);
    });
  },
  deleteSubscription(_, subscription) {
    return SubscriptionsApi.delete(subscription.id);
  },
  fetchSubscriptions({ commit }) {
    SubscriptionsApi.list().then((response) => {
      commit(MutationTypes.SET_SUBSCRIPTIONS, response.data);
    });
  },
  fetchSubscription({ commit, state }) {
    SubscriptionsApi.get(state.route.params.subscriptionId).then((response) => {
      commit(MutationTypes.SET_SUBSCRIPTION, response.data);
    });
  },

  // Authentication
  async signIn({ state, dispatch }) {
    delete axios.defaults.headers.Authorization;
    delete localStorage.authorization;
    const response = await AuthenticationApi.signIn(state.signUp);
    localStorage.authorization = response.headers.authorization;
    axios.defaults.headers.Authorization = response.headers.authorization;
    const promise = dispatch('fetchMe');
    return promise;
  },
  async sendSignUpForm({ state, dispatch }, { method, afterLink }) {
    const studentForm = { ...state.signUp };
    if (state.signUp.consent) {
      studentForm.consents_attributes = [{
        kind: "Droit à l'image",
        date: new Date(),
      }];
    }
    await method(studentForm);
    dispatch('fetchMe');
    if (!isEmpty(state.pendingSubscription)) {
      dispatch('confirmPendingSubscription');
    } else {
      router.push(afterLink);
    }
  },
  async signUp({ dispatch }) {
    await dispatch('sendSignUpForm', { method: AuthenticationApi.signUp, afterLink: '/confirmation-email-sent' });
  },
  async signUpAfterCasAuthentication({ dispatch }) {
    await dispatch('sendSignUpForm', { method: StudentApi.update, afterLink: '/' });
  },
  signOut({ commit }) {
    delete axios.defaults.headers.Authorization;
    delete localStorage.authorization;
    AuthenticationApi.signOut().then(() => {
      commit(MutationTypes.SET_CURRENT_STUDENT, { data: { attributes: {} } });
      router.push('/courses');
    });
  },
  async resetPassword({ state, commit }) {
    try {
      const response = await AuthenticationApi.resetPassword(state.signUp.email);
      let alertText; let
        alertColor;
      if (response.data.message === 'Confirmation instructions resent.') {
        alertText = 'Your email is not confirmed';
        alertColor = 'warning';
      } else {
        alertText = 'Reset password instructions';
        alertColor = 'success';
      }
      commit(MutationTypes.SET_ALERT, {
        show: true,
        text: i18n.t(alertText),
        color: alertColor,
        timeout: 5000,
      });
    } catch (e) {
      commit(MutationTypes.SET_ALERT, {
        show: true,
        text: i18n.t('An error has occured'),
        color: 'error',
        timeout: 3000,
      });
    }
  },
  changePassword({ state, commit }, token) {
    return AuthenticationApi.changePassword(state.signUp.password, token).then(() => {
      router.push('/sign_in');
      commit(MutationTypes.SET_ALERT, {
        show: true,
        text: i18n.t('Your password has been changed'),
        color: 'success',
        timeout: 10000,
      });
    }).catch(() => {
      commit(MutationTypes.SET_ALERT, {
        show: true,
        text: i18n.t('An error has occured'),
        color: 'error',
        timeout: 3000,
      });
    });
  },

  // Profile
  async fetchMe({ commit }) {
    const response = await StudentApi.fetchMe();
    commit(MutationTypes.SET_CURRENT_STUDENT, response.data);
  },
  async fetchMeAndFillSignUpForm({ commit }) {
    const response = await StudentApi.fetchMe();
    commit(MutationTypes.SET_SIGN_UP_FORM, response.data.data.attributes);
  },
  uploadAvatar({ commit }, file) {
    StudentApi.updateAvatar(file).then((response) => {
      commit(MutationTypes.SET_CURRENT_STUDENT, response.data);
    });
  },
  deleteAvatar() {
    StudentApi.deleteAvatar();
  },
  updateEmail({ state, commit }) {
    const params = {
      student: {
        email: state.currentStudent.data.attributes.email,
      },
    };

    StudentApi.update(params).then((response) => {
      commit(MutationTypes.SET_CURRENT_STUDENT, response.data);
    });
  },
  fetchPrograms({ commit }) {
    ProgramApi.list().then((response) => {
      commit(MutationTypes.SET_PROGRAMS, response.data);
    });
  },
  async confirmEmail(_, { token }) {
    const response = await AuthenticationApi.confirm({ token });
    delete axios.defaults.headers.Authorization;
    delete localStorage.authorization;
    localStorage.authorization = response.headers.authorization;
    axios.defaults.headers.Authorization = response.headers.authorization;
    return response;
  },

  async validateTicket({ state, dispatch }, ticket) {
    const response = await AuthenticationApi.validateTicket(ticket);
    delete axios.defaults.headers.Authorization;
    delete localStorage.authorization;
    localStorage.authorization = response.headers.authorization;
    axios.defaults.headers.Authorization = response.headers.authorization;
    if (!isEmpty(state.pendingSubscription)) {
      dispatch('confirmPendingSubscription');
    } else if (response.data.data.attributes.program_id) {
      router.push('/');
    } else {
      router.push('/sign_up?incomplete=true');
    }
  },

  async fetchSettings() {
    return CheckApi.check();
  },
};

export default actions;
